/**
 * Forgot password (PUBLIC) — request a reset link by email. Reachable from the login screen's
 * "Forgot password?" link. The backend never reveals whether an address has an account, so on
 * success we ALWAYS show the same neutral confirmation (no user enumeration).
 *
 * Design system only (Screen / Field / Button / Text). No @smartbox/* (mobile invariant 1).
 */
import { useState } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { forgotPassword } from '../src/api/client';
import { ApiRequestError } from '../src/api/client';
import { Screen, Text, Field, Button } from '../src/components/ui';
import { BeamLogo } from '../src/components/brand/Logo';
import { theme } from '../src/theme';

const IS_WEB = Platform.OS === 'web';

export default function ForgotPasswordScreen() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    const trimmed = email.trim();
    setError(null);
    if (!trimmed || !trimmed.includes('@')) {
      setError('Enter the email address for your account.');
      return;
    }
    setBusy(true);
    try {
      const client: 'web' | 'ios' | 'android' = IS_WEB ? 'web' : Platform.OS === 'android' ? 'android' : 'ios';
      await forgotPassword(trimmed, client);
      setSent(true);
    } catch (e) {
      // The endpoint is designed to always succeed; only a network/parse failure lands here.
      setError(e instanceof ApiRequestError ? e.message : 'Could not send the reset email. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={styles.container}>
          <View style={styles.brand}>
            <BeamLogo size={40} />
            <Text variant="title">Reset your password</Text>
            <Text variant="subhead" color="secondary">
              Enter your account email and we'll send you a link to set a new password.
            </Text>
          </View>

          {sent ? (
            <View style={styles.form}>
              <Text variant="body">
                If an account exists for {email.trim()}, a reset link is on its way. The link expires in 1 hour.
              </Text>
              <Text variant="small" color="secondary">
                Didn't get it? Check your spam folder, or try again in a few minutes.
              </Text>
              <Button title="Back to sign in" onPress={() => router.replace('/login')} size="lg" style={styles.submit} accessibilityLabel="Back to sign in" />
            </View>
          ) : (
            <View style={styles.form}>
              <Field
                label="Email"
                value={email}
                onChangeText={setEmail}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                textContentType="emailAddress"
                autoComplete="email"
                placeholder="you@example.com"
                editable={!busy}
                accessibilityLabel="Email address"
                returnKeyType="done"
                onSubmitEditing={onSubmit}
                error={error ?? undefined}
              />
              <Button title="Send reset link" onPress={onSubmit} loading={busy} disabled={busy} size="lg" style={styles.submit} accessibilityLabel="Send reset link" />
              <Button title="Back to sign in" variant="ghost" size="sm" onPress={() => router.replace('/login')} accessibilityLabel="Back to sign in" />
            </View>
          )}
        </View>
      </KeyboardAvoidingView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, justifyContent: 'center', gap: theme.spacing['4xl'] },
  brand: { alignItems: 'center', gap: theme.spacing.md },
  form: { gap: theme.spacing.md },
  submit: { marginTop: theme.spacing.sm },
});
